import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import logoIconPng from './logo-icon.svg';

const planos = {
  start: {
    codigo: 'start',
    nome: 'Start',
    empresas: 1,
    valor: 189,
    destaque: 'Ideal para começar',
    descricao: 'Para uma ótica organizar atendimento, OS, clientes e pós-venda sem complicar a rotina.',
    recursos: [
      '1 empresa / CNPJ',
      'Usuários da equipe',
      'Ordens de serviço completas',
      'Visualização de fotos do cliente',
      'CRM integrado',
      'Avisos por mensagens',
      'Fluxo de caixa',
      'Régua de relacionamento',
      'Controle de estoque',
      'Estoque de armações',
      'Emissão de NFC-e por OS'
    ]
  },
  crescimento: {
    codigo: 'crescimento',
    nome: 'Crescimento',
    empresas: 2,
    valor: 299,
    destaque: 'Mais escolhido',
    descricao: 'Para quem tem duas unidades e precisa acompanhar tudo com separação e clareza.',
    recursos: [
      'Até 2 empresas / CNPJs',
      'Dados separados por empresa',
      'Relatórios por unidade',
      'CRM por empresa',
      'Campanhas por loja',
      'Fluxo de caixa por unidade',
      'DRE operacional',
      'Controle de usuários',
      'Melhor custo por operação'
    ]
  },
  multiempresas: {
    codigo: 'multiempresas',
    nome: 'Multiempresas',
    empresas: 3,
    valor: 399,
    destaque: 'Para redes',
    descricao: 'Para redes pequenas que querem crescer com controle, padrão e visão centralizada.',
    recursos: [
      'Até 3 empresas / CNPJs',
      'Visão consolidada da rede',
      'Permissões por empresa',
      'Estoque por unidade',
      'CRM por empresa',
      'Campanhas segmentadas',
      'NFC-e por empresa',
      'Dashboard gerencial',
      'Base pronta para expansão'
    ]
  }
};

const STORAGE_KEY = 'lumix_checkout_draft_v5';
const VERIFIED_STORAGE_KEY = 'lumix_checkout_verificado';
const CHECKOUT_VERSION = 'checkout-lumix-v2026-05-28';
const LEGAL_DOCUMENTS = [
  { key: 'termosUso', label: 'Termos de Uso', path: '/termos-de-uso', version: '2026-05-28' },
  { key: 'politicaPrivacidade', label: 'Política de Privacidade', path: '/politica-de-privacidade', version: '2026-05-28' },
  { key: 'politicaCookies', label: 'Política de Cookies', path: '/politica-de-cookies', version: '2026-05-28' },
  { key: 'condicoesComerciais', label: 'Condições Comerciais', path: '/condicoes-comerciais', version: '2026-05-28' }
];

function normalizarPlano(plano) {
  const codigo = String(plano || 'start').trim().toLowerCase();
  return planos[codigo] ? codigo : 'start';
}

function pegarApiUrl() {
  return String(process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
}

async function lerRespostaJson(response) {
  const texto = await response.text();
  if (!texto) return {};
  try { return JSON.parse(texto); } catch { return { error: texto }; }
}

function limparTelefone(valor) { return String(valor || '').replace(/\D/g, ''); }

function formatarWhatsapp(valor) {
  const digits = limparTelefone(valor).slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function normalizarEmail(email) { return String(email || '').trim().toLowerCase(); }
function normalizarCupom(cupom) { return String(cupom || '').trim().toUpperCase().replace(/\s+/g, ''); }
function emailValido(email) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizarEmail(email)); }
function somenteNumeros(valor, limite = 6) { return String(valor || '').replace(/\D/g, '').slice(0, limite); }

function formatarMoeda(valor) {
  return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function mercadoPagoEhSandbox(data = {}) {
  return (
    data?.ambiente === 'sandbox' || data?.environment === 'sandbox' || data?.sandbox === true ||
    process.env.REACT_APP_MERCADO_PAGO_ENV === 'sandbox' || process.env.REACT_APP_USE_MP_SANDBOX === 'true'
  );
}

function escolherCheckoutUrl(data = {}) {
  const sandbox = mercadoPagoEhSandbox(data);
  if (sandbox) return (data?.sandbox_init_point || data?.sandboxInitPoint || data?.checkoutUrl || data?.paymentUrl || data?.init_point || data?.initPoint || '');
  return (data?.checkoutUrl || data?.paymentUrl || data?.init_point || data?.initPoint || data?.sandbox_init_point || data?.sandboxInitPoint || '');
}

function montarErroInteligente(error, contexto = '') {
  const status = error?.status || error?.response?.status || null;
  const mensagem = String(error?.message || error?.error || '').trim();

  if (status === 0 || mensagem.includes('Failed to fetch') || mensagem.includes('NetworkError')) return 'Não consegui conectar com o servidor. Verifique sua internet.';
  if (status === 400) return mensagem || 'Alguma informação está incompleta. Revise os campos.';
  if (status === 401 || status === 403) return mensagem || 'Não foi possível validar sua solicitação.';
  if (status === 404) return mensagem || 'Registro não encontrado.';
  if (status === 409) return mensagem || 'Esse e-mail já possui uma conta ativa. Faça login.';
  if (Number(status) >= 500) return mensagem || 'O servidor encontrou um problema. Tente novamente em instantes.';
  if (mensagem) return mensagem;

  if (contexto === 'email') return 'Não foi possível enviar o código agora.';
  if (contexto === 'codigo') return 'Não foi possível confirmar o código.';
  if (contexto === 'cupom') return 'Não foi possível validar o cupom.';
  if (contexto === 'checkout') return 'Não foi possível iniciar o pagamento.';
  return 'Ocorreu um erro inesperado. Tente novamente.';
}

function salvarRascunhoSeguro(dados) {
  try {
    const { senha, confirmarSenha, ...dadosSeguros } = dados;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dadosSeguros));
  } catch {}
}

function carregarRascunhoSeguro() {
  try {
    const salvo = localStorage.getItem(STORAGE_KEY);
    return salvo ? JSON.parse(salvo) : null;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function montarVersoesLegais() {
  return LEGAL_DOCUMENTS.reduce((acc, doc) => {
    acc[doc.key] = { label: doc.label, path: doc.path, version: doc.version };
    return acc;
  }, {});
}

export default function CheckoutStart() {
  const { plano } = useParams();
  const navigate = useNavigate();

  const [isInitializing, setIsInitializing] = useState(true);
  const planoCodigo = useMemo(() => normalizarPlano(plano), [plano]);
  const planoSelecionado = planos[planoCodigo];

  const otpInputRef = useRef(null);
  const loginTimerRef = useRef(null);
  const autoConfirmandoRef = useRef(false);
  const formTopRef = useRef(null);

  const [form, setForm] = useState({
    nomeResponsavel: '', email: '', whatsapp: '', senha: '', confirmarSenha: '', nomeEmpresa: ''
  });

  const [codigoEmail, setCodigoEmail] = useState('');
  const [codigoEnviado, setCodigoEnviado] = useState(false);
  const [emailConfirmado, setEmailConfirmado] = useState(false);
  const [emailVerificationToken, setEmailVerificationToken] = useState('');
  const [emailAtivo, setEmailAtivo] = useState(false);

  const [pendenciaPagamento, setPendenciaPagamento] = useState(null);
  const [aceitouDocumentos, setAceitouDocumentos] = useState(false);
  const [aceitouRecorrencia, setAceitouRecorrencia] = useState(false);
  const [aceitouMarketing, setAceitouMarketing] = useState(false);

  const [cupomCodigo, setCupomCodigo] = useState('');
  const [cupomAplicado, setCupomAplicado] = useState(null);
  const [cupomErro, setCupomErro] = useState('');
  const [validandoCupom, setValidandoCupom] = useState(false);

  const [showMobileSummary, setShowMobileSummary] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);

  const [loading, setLoading] = useState(false);
  const [enviandoCodigo, setEnviandoCodigo] = useState(false);
  const [confirmandoCodigo, setConfirmandoCodigo] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState('');

  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const [cooldown, setCooldown] = useState(0);
  const [tentativasCodigo, setTentativasCodigo] = useState(0);
  const [redirecionandoLogin, setRedirecionandoLogin] = useState(false);

  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmarSenha, setMostrarConfirmarSenha] = useState(false);

  const emailAtual = useMemo(() => normalizarEmail(form.email), [form.email]);
  const whatsappLimpo = useMemo(() => limparTelefone(form.whatsapp), [form.whatsapp]);

  const etapaBloqueada = !emailConfirmado || emailAtivo || !!checkoutUrl || redirecionandoLogin;

  const resumoPlano = useMemo(() => {
    if (cupomAplicado?.cupomAplicado) {
      return {
        valorOriginal: Number(cupomAplicado.valorOriginal || planoSelecionado.valor),
        descontoValor: Number(cupomAplicado.descontoValor || 0),
        valorFinal: Number(cupomAplicado.valorFinal || planoSelecionado.valor)
      };
    }
    return { valorOriginal: planoSelecionado.valor, descontoValor: 0, valorFinal: planoSelecionado.valor };
  }, [cupomAplicado, planoSelecionado]);

  const descontoPrimeiroMesAtivo = Number(resumoPlano.descontoValor || 0) > 0;
  const valorMensalNormal = Number(planoSelecionado.valor || 0);
  const valorPrimeiraCobranca = Number(resumoPlano.valorFinal || valorMensalNormal);

  useEffect(() => {
    const timer = setTimeout(() => setIsInitializing(false), 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const salvo = carregarRascunhoSeguro();
    if (salvo) {
      setForm((prev) => ({ ...prev, ...salvo, senha: '', confirmarSenha: '' }));
      if (salvo.cupomCodigo) {
        setCupomCodigo(normalizarCupom(salvo.cupomCodigo));
        setShowCoupon(true);
      }
    }
    return () => { if (loginTimerRef.current) clearTimeout(loginTimerRef.current); };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((p) => Math.max(p - 1, 0)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    setCupomAplicado(null);
    setCupomErro('');
  }, [planoCodigo, emailAtual]);

  function limparMensagens() { setErro(''); setSucesso(''); }

  function resetarVerificacaoEmail({ manterCodigoEnviado = false } = {}) {
    setCodigoEmail(''); setEmailConfirmado(false); setEmailVerificationToken('');
    setEmailAtivo(false); setPendenciaPagamento(null); setCheckoutUrl('');
    setSucesso(''); setTentativasCodigo(0); setRedirecionandoLogin(false);
    setCupomAplicado(null); setCupomErro(''); setAceitouDocumentos(false); setAceitouRecorrencia(false); setAceitouMarketing(false);
    if (!manterCodigoEnviado) setCodigoEnviado(false);
  }

  function handleChange(event) {
    const { name, value } = event.target;
    limparMensagens();
    if (name === 'email') resetarVerificacaoEmail();
    setForm((prev) => {
      const newState = { ...prev, [name]: name === 'whatsapp' ? formatarWhatsapp(value) : value };
      if (name !== 'senha' && name !== 'confirmarSenha') salvarRascunhoSeguro({ ...newState, cupomCodigo });
      return newState;
    });
  }

  function handlePlanoChange(novoPlano) {
    const codigo = normalizarPlano(novoPlano);
    if (codigo === planoCodigo) return;
    limparMensagens();
    setCupomAplicado(null); setCupomErro('');
    navigate(`/checkout/${codigo}`, { replace: true });
  }

  function tratarContaAtiva(data = {}) {
    setEmailAtivo(true); setEmailConfirmado(true); setPendenciaPagamento(null); setSucesso('');
    setErro('Este e-mail já possui conta ativa. Redirecionando para login...');
    try { sessionStorage.setItem('lumix_login_email', JSON.stringify({ email: emailAtual, motivo: 'conta_ativa' })); } catch {}
    setRedirecionandoLogin(true);
    if (loginTimerRef.current) clearTimeout(loginTimerRef.current);
    loginTimerRef.current = setTimeout(() => navigate('/login'), 2500);
  }

  function tratarPagamentoPendente(data = {}) {
    const payload = { ...data, email: emailAtual, emailVerificationToken: data?.verificationToken || emailVerificationToken };
    setPendenciaPagamento(payload);
    setErro(''); 
    if (data?.cupom?.codigo) {
      setCupomCodigo(data.cupom.codigo); setShowCoupon(true);
      setCupomAplicado({
        cupomAplicado: true, codigo: data.cupom.codigo,
        valorOriginal: data.valorOriginal || data.valorMensal,
        descontoValor: data.descontoValor || data.cupom.descontoValor || 0,
        valorFinal: data.valorFinal || data.valorMensal
      });
    }
    try { sessionStorage.setItem(VERIFIED_STORAGE_KEY, JSON.stringify(payload)); } catch {}
  }

  function continuarPagamentoPendente() {
    if (!pendenciaPagamento) return;
    setLoading(true);
    const url = escolherCheckoutUrl(pendenciaPagamento);
    if (url) {
      setCheckoutUrl(url);
      window.location.href = url;
    } else {
      navigate(`/checkout/continuar?email=${encodeURIComponent(emailAtual)}&token=${encodeURIComponent(emailVerificationToken)}`);
    }
  }

  async function validarCupom() {
    const codigo = normalizarCupom(cupomCodigo);
    limparMensagens(); setCupomErro('');
    if (!codigo) { setCupomErro('Digite um cupom.'); return; }
    if (!emailValido(emailAtual)) { setCupomErro('Confirme seu e-mail antes.'); return; }

    try {
      setValidandoCupom(true);
      const res = await fetch(`${pegarApiUrl()}/api/checkout/cupom/validar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailAtual, planoCodigo, cupomCodigo: codigo })
      });
      const data = await lerRespostaJson(res);
      if (!res.ok) throw new Error(data?.error || data?.message || 'Cupom inválido.');
      setCupomCodigo(codigo); setCupomAplicado(data); setSucesso('Cupom aplicado com sucesso!');
      salvarRascunhoSeguro({ ...form, cupomCodigo: codigo });
    } catch (error) {
      setCupomAplicado(null); setCupomErro(montarErroInteligente(error, 'cupom'));
    } finally { setValidandoCupom(false); }
  }

  function removerCupom() {
    setCupomCodigo(''); setCupomAplicado(null); setCupomErro('');
    salvarRascunhoSeguro({ ...form, cupomCodigo: '' });
  }

  async function enviarCodigoEmail() {
    if (enviandoCodigo || cooldown > 0 || !emailValido(emailAtual)) return;
    limparMensagens();
    try {
      setEnviandoCodigo(true);
      const res = await fetch(`${pegarApiUrl()}/api/checkout/email/enviar-codigo`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailAtual })
      });
      const data = await lerRespostaJson(res);
      if (!res.ok) throw new Error(data?.error || data?.message || 'Erro ao enviar.');
      resetarVerificacaoEmail({ manterCodigoEnviado: true });
      setCodigoEnviado(true); setCooldown(Number(data?.cooldown || 45));
      setTimeout(() => otpInputRef.current?.focus(), 100);
    } catch (error) { setErro(montarErroInteligente(error, 'email')); } 
    finally { setEnviandoCodigo(false); }
  }

  async function confirmarCodigoEmail(codigoManual = codigoEmail) {
    const codigo = somenteNumeros(codigoManual, 6);
    if (confirmandoCodigo || autoConfirmandoRef.current || codigo.length !== 6) return;
    limparMensagens();
    try {
      autoConfirmandoRef.current = true; setConfirmandoCodigo(true);
      const res = await fetch(`${pegarApiUrl()}/api/checkout/email/confirmar-codigo`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailAtual, codigo })
      });
      const data = await lerRespostaJson(res);
      if (!res.ok) throw new Error(data?.error || 'Código inválido.');
      
      const token = data?.verificationToken || data?.emailVerificationToken || '';
      setEmailConfirmado(true); setEmailVerificationToken(token); setTentativasCodigo(0);
      
      if (data?.tipo === 'conta_ativa' || data?.status === 'ativado') return tratarContaAtiva(data);
      if (data?.tipo === 'pagamento_pendente' || data?.status === 'pendente_pagamento' || data?.checkoutUrl) return tratarPagamentoPendente({ ...data, verificationToken: token });
      
      setSucesso('E-mail verificado com sucesso.');
    } catch (error) {
      setTentativasCodigo(p => p + 1); setCodigoEmail('');
      if (tentativasCodigo + 1 >= 3) {
        setErro('Código expirado. Reenvie para continuar.'); setCodigoEnviado(false); setCooldown(0);
      } else { setErro(montarErroInteligente(error, 'codigo')); }
    } finally { setConfirmandoCodigo(false); autoConfirmandoRef.current = false; }
  }

  function handleCodigoChange(e) {
    const val = somenteNumeros(e.target.value, 6);
    setCodigoEmail(val); limparMensagens();
    if (val.length === 6 && !confirmandoCodigo) setTimeout(() => confirmarCodigoEmail(val), 80);
  }

  function validarFormulario() {
    if (!emailConfirmado) return 'Verifique seu e-mail antes de continuar.';
    if (form.nomeResponsavel.trim().length < 3) return 'Informe seu nome completo.';
    if (whatsappLimpo.length < 10) return 'Informe um telefone válido.';
    if (form.nomeEmpresa.trim().length < 2) return 'Informe o nome da ótica.';
    if (form.senha.length < 6) return 'A senha deve ter pelo menos 6 caracteres.';
    if (form.senha !== form.confirmarSenha) return 'As senhas não conferem.';
    if (!aceitouDocumentos || !aceitouRecorrencia) return 'Aceite os termos para prosseguir.';
    return '';
  }

  function montarAceiteLegalPayload() {
    const documentos = montarVersoesLegais();
    return {
      termos: true, politicaPrivacidade: true, politicaCookies: true, condicoesComerciais: true,
      versao: CHECKOUT_VERSION, aceitoEm: new Date().toISOString(),
      emailAceito: emailAtual, documentos, versoesDocumentos: documentos,
      marcadores: {
        termosUso: aceitouDocumentos, recorrenciaMensal: aceitouRecorrencia,
        comunicacoesMarketing: aceitouMarketing
      }
    };
  }

  async function iniciarPagamento() {
    const erroValidacao = validarFormulario();
    if (erroValidacao) { setErro(erroValidacao); formTopRef.current?.scrollIntoView({ behavior: 'smooth' }); return; }
    
    try {
      setLoading(true); limparMensagens();
      const payload = {
        nomeResponsavel: form.nomeResponsavel.trim(), email: emailAtual, whatsapp: whatsappLimpo,
        senha: form.senha, nomeEmpresa: form.nomeEmpresa.trim(), planoCodigo, cupomCodigo: cupomAplicado?.codigo || '',
        emailVerificationToken, aceiteLegal: montarAceiteLegalPayload()
      };
      
      const res = await fetch(`${pegarApiUrl()}/api/checkout/iniciar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      const data = await lerRespostaJson(res);
      if (!res.ok) throw new Error(data?.error || 'Erro ao iniciar pagamento.');
      
      const url = escolherCheckoutUrl(data);
      if (url) {
        setCheckoutUrl(url);
        window.location.href = url;
        return;
      }
      throw new Error('Servidor não retornou a URL do Mercado Pago.');
    } catch (error) { setLoading(false); setErro(montarErroInteligente(error, 'checkout')); }
  }

  function handleSubmit(e) { e.preventDefault(); iniciarPagamento(); }

  const podeEnviarCodigo = emailValido(emailAtual) && !enviandoCodigo && cooldown <= 0 && !emailConfirmado;
  const podePagar = emailConfirmado && aceitouDocumentos && aceitouRecorrencia && !loading && !checkoutUrl && !pendenciaPagamento;

  // Seletor visual de planos — mantém a mesma lógica/rota do checkout original
  const PlanSelectorUI = (
    <div className="gm-plan-selector">
      <div className="gm-section-label-row">
        <label className="gm-label-small">Plano selecionado</label>
        <span className="gm-section-hint">Você pode alterar</span>
      </div>

      <div className="gm-plan-grid">
        {Object.values(planos).map((item) => (
          <button
            key={item.codigo}
            type="button"
            className={`gm-plan-chip ${item.codigo === planoCodigo ? 'active' : ''}`}
            onClick={() => handlePlanoChange(item.codigo)}
          >
            <span className="gm-plan-chip-check">
              {item.codigo === planoCodigo ? <CheckIcon color="#fff" /> : null}
            </span>
            <span className="gm-plan-chip-copy">
              <strong>{item.nome}</strong>
              <small>{item.empresas === 1 ? '1 empresa' : `Até ${item.empresas} empresas`}</small>
            </span>
            <span className="gm-plan-chip-price">
              {formatarMoeda(item.valor)}
              <small>/mês</small>
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <style>{css}</style>
      
      {/* 1. Page Load Indicator */}
      <div className={`gm-loader-overlay ${isInitializing ? 'active' : ''}`}>
        <div className="gm-loader-content">
          <SpinnerIcon className="gm-spinner-large" />
          <h2 className="gm-loader-text">Preparando ambiente seguro...</h2>
        </div>
      </div>

      <header className="gm-header">
        <div className="gm-header-inner">
          <Link to="/" className="gm-logo">
            <img src={logoIconPng} alt="Lumix" />
            <span className="gm-brand-wordmark">
              Lumix&nbsp;
              <span style={{ color: 'var(--gm-blue)' }}>Ó</span>
              <span style={{ color: 'var(--gm-error)' }}>t</span>
              <span style={{ color: 'var(--gm-yellow)' }}>i</span>
              <span style={{ color: 'var(--gm-success)' }}>c</span>
              <span style={{ color: 'var(--gm-blue)' }}>a</span>
              <span style={{ color: 'var(--gm-error)' }}>s</span>
            </span>
          </Link>
          <div className="gm-secure-badge"><span className="gm-secure-dot" /><LockIcon /> Pagamento protegido</div>
        </div>
      </header>

      <div className="gm-layout">
        
        {/* Mobile Accordion Summary */}
        <div className="gm-mobile-summary">
          <button className="gm-mobile-summary-toggle" onClick={() => setShowMobileSummary(!showMobileSummary)}>
            <span className="toggle-left">
              <CartIcon />
              <span>{showMobileSummary ? 'Ocultar resumo' : 'Ver resumo do pedido'}</span>
              <ChevronIcon isOpen={showMobileSummary} />
            </span>
            <span className="toggle-right">{formatarMoeda(valorPrimeiraCobranca)}</span>
          </button>
          
          {showMobileSummary && (
            <div className="gm-summary-content">
              {PlanSelectorUI}
              <SummaryDetails 
                planoSelecionado={planoSelecionado} valorMensalNormal={valorMensalNormal} 
                descontoPrimeiroMesAtivo={descontoPrimeiroMesAtivo} resumoPlano={resumoPlano}
                cupomCodigo={cupomCodigo} setCupomCodigo={setCupomCodigo} cupomAplicado={cupomAplicado}
                cupomErro={cupomErro} validarCupom={validarCupom} removerCupom={removerCupom}
                validandoCupom={validandoCupom} showCoupon={showCoupon} setShowCoupon={setShowCoupon}
              />
            </div>
          )}
        </div>

        <div className="gm-columns">
          
          {/* Main Form Left Column */}
          <main className="gm-main" ref={formTopRef}>
            <div className="gm-title-area">
              <div className="gm-eyebrow">Ativação da conta</div>
              <h1 className="gm-h1">Finalize sua assinatura</h1>
              <p className="gm-p">Confirme seu e-mail, complete os dados da ótica e siga para o pagamento seguro.</p>

              <div className="gm-trust-row">
                <span><CheckCircleIcon /> Dados protegidos</span>
                <span><LockIcon /> Checkout Mercado Pago</span>
              </div>
            </div>

            {erro && <div className="gm-alert gm-alert-error"><AlertIcon /> <div>{erro}</div></div>}
            {sucesso && !pendenciaPagamento && <div className="gm-alert gm-alert-success"><CheckCircleIcon /> <div>{sucesso}</div></div>}

            {pendenciaPagamento && !emailAtivo && (
              <div className="gm-alert gm-alert-warning fade-in">
                <InfoIcon />
                <div>
                  <strong style={{ display: 'block', marginBottom: '4px' }}>Pagamento pendente encontrado</strong>
                  <p style={{ margin: 0 }}>Você já havia iniciado uma assinatura. Continue de onde parou ou gere uma nova cobrança.</p>
                  <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button type="button" className="gm-btn gm-btn-primary gm-btn-small" onClick={continuarPagamentoPendente} disabled={loading || !!checkoutUrl}>
                      {loading ? <SpinnerIcon /> : 'Continuar pagamento'}
                    </button>
                    <button type="button" className="gm-btn gm-btn-outline gm-btn-small" onClick={() => setPendenciaPagamento(null)} disabled={loading || !!checkoutUrl}>Gerar novo</button>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="gm-form-wrapper" noValidate>
              
              {/* STEP 1: Account / Email */}
              <div className={`gm-card ${emailConfirmado ? 'gm-card-done' : ''}`}>
                <div className="gm-card-header">
                  <div className={`gm-step-circle ${emailConfirmado ? 'done' : ''}`}>{emailConfirmado ? <CheckIcon color="#fff" /> : '1'}</div>
                  <h2 className="gm-h2">Conta e Validação</h2>
                </div>
                <div className="gm-card-body">
                  <div className="gm-input-group">
                    <label>E-mail corporativo ou pessoal</label>
                    <div className="gm-input-action-row">
                      <input type="email" name="email" value={form.email} onChange={handleChange} disabled={emailConfirmado || redirecionandoLogin} placeholder="voce@otica.com.br" className="gm-input" />
                      {!emailConfirmado && (
                        <button type="button" className="gm-btn gm-btn-outline" onClick={enviarCodigoEmail} disabled={!podeEnviarCodigo}>
                          {enviandoCodigo ? <SpinnerIcon /> : cooldown > 0 ? `${cooldown}s` : 'Verificar'}
                        </button>
                      )}
                    </div>
                  </div>

                  {codigoEnviado && !emailConfirmado && (
                    <div className="gm-otp-box">
                      <label>Código de 6 dígitos enviado ao e-mail</label>
                      <div style={{ position: 'relative', maxWidth: '180px' }}>
                        <input ref={otpInputRef} type="text" inputMode="numeric" value={codigoEmail} onChange={handleCodigoChange} maxLength={6} placeholder="000000" className="gm-input gm-otp-input" disabled={confirmandoCodigo} />
                        {confirmandoCodigo && <SpinnerIcon className="gm-otp-spinner" />}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* STEP 2: Details */}
              <div className={`gm-card ${!emailConfirmado || emailAtivo ? 'gm-card-disabled' : ''}`}>
                <div className="gm-card-header">
                  <div className="gm-step-circle">2</div>
                  <h2 className="gm-h2">Dados da Ótica</h2>
                </div>
                <div className="gm-card-body">
                  <div className="gm-grid-2">
                    <div className="gm-input-group">
                      <label>Nome completo</label>
                      <input type="text" name="nomeResponsavel" value={form.nomeResponsavel} onChange={handleChange} disabled={etapaBloqueada} className="gm-input" placeholder="João Silva" />
                    </div>
                    <div className="gm-input-group">
                      <label>Telefone / WhatsApp</label>
                      <input type="tel" name="whatsapp" value={form.whatsapp} onChange={handleChange} disabled={etapaBloqueada} className="gm-input" placeholder="(11) 90000-0000" />
                    </div>
                  </div>
                  <div className="gm-input-group" style={{ marginTop: '16px' }}>
                    <label>Nome da ótica</label>
                    <input type="text" name="nomeEmpresa" value={form.nomeEmpresa} onChange={handleChange} disabled={etapaBloqueada} className="gm-input" placeholder="Ótica Exemplo" />
                  </div>
                  <div className="gm-grid-2" style={{ marginTop: '16px' }}>
                    <div className="gm-input-group">
                      <label>Criar senha</label>
                      <div className="gm-pwd-wrapper">
                        <input type={mostrarSenha ? 'text' : 'password'} name="senha" value={form.senha} onChange={handleChange} disabled={etapaBloqueada} className="gm-input" placeholder="Mínimo 6 caracteres" />
                        <button type="button" onClick={() => setMostrarSenha(!mostrarSenha)} className="gm-pwd-toggle">{mostrarSenha ? <EyeOffIcon /> : <EyeIcon />}</button>
                      </div>
                    </div>
                    <div className="gm-input-group">
                      <label>Confirmar senha</label>
                      <div className="gm-pwd-wrapper">
                        <input type={mostrarConfirmarSenha ? 'text' : 'password'} name="confirmarSenha" value={form.confirmarSenha} onChange={handleChange} disabled={etapaBloqueada} className="gm-input" placeholder="Repita a senha" />
                        <button type="button" onClick={() => setMostrarConfirmarSenha(!mostrarConfirmarSenha)} className="gm-pwd-toggle">{mostrarConfirmarSenha ? <EyeOffIcon /> : <EyeIcon />}</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* STEP 3: Legal & Payment Action */}
              <div className={`gm-card ${!emailConfirmado || emailAtivo ? 'gm-card-disabled' : ''}`}>
                <div className="gm-card-header">
                  <div className="gm-step-circle">3</div>
                  <h2 className="gm-h2">Termos e Pagamento</h2>
                </div>
                <div className="gm-card-body">
                  <label className="gm-checkbox-row">
                    <input type="checkbox" checked={aceitouDocumentos} onChange={(e) => setAceitouDocumentos(e.target.checked)} disabled={etapaBloqueada} />
                    <span>Li e aceito os <Link to="/termos-de-uso" target="_blank">Termos de Uso</Link> e a <Link to="/politica-de-privacidade" target="_blank">Política de Privacidade</Link>.</span>
                  </label>
                  <label className="gm-checkbox-row">
                    <input type="checkbox" checked={aceitouRecorrencia} onChange={(e) => setAceitouRecorrencia(e.target.checked)} disabled={etapaBloqueada} />
                    <span>Entendi que a assinatura é <strong>mensal e recorrente</strong>, no valor normal de {formatarMoeda(valorMensalNormal)}/mês.</span>
                  </label>
                  <label className="gm-checkbox-row">
                    <input type="checkbox" checked={aceitouMarketing} onChange={(e) => setAceitouMarketing(e.target.checked)} disabled={etapaBloqueada} />
                    <span>Quero receber novidades, ofertas e comunicações (Opcional).</span>
                  </label>
                  
                  <div className="gm-desktop-action">
                    {/* 2. Payment Loading Indicator on Button */}
                    <button type="submit" className={`gm-btn gm-btn-primary gm-btn-large ${loading || checkoutUrl ? 'loading' : ''}`} disabled={!podePagar}>
                      {loading || checkoutUrl ? <><SpinnerIcon /> Processando ambiente seguro...</> : <><LockIcon color="#fff" /> Ir para pagamento seguro</>}
                    </button>
                    <p className="gm-footer-note">Ambiente seguro processado pelo <strong>Mercado Pago</strong>.</p>
                  </div>
                </div>
              </div>

            </form>
          </main>

          {/* Right Column Summary (Desktop) */}
          <aside className="gm-sidebar">
            <div className="gm-sidebar-sticky">
              <h3 className="gm-sidebar-title">Resumo do Pedido</h3>
              
              {PlanSelectorUI}

              <SummaryDetails 
                planoSelecionado={planoSelecionado} valorMensalNormal={valorMensalNormal} 
                descontoPrimeiroMesAtivo={descontoPrimeiroMesAtivo} resumoPlano={resumoPlano}
                cupomCodigo={cupomCodigo} setCupomCodigo={setCupomCodigo} cupomAplicado={cupomAplicado}
                cupomErro={cupomErro} validarCupom={validarCupom} removerCupom={removerCupom}
                validandoCupom={validandoCupom} showCoupon={showCoupon} setShowCoupon={setShowCoupon}
              />
            </div>
          </aside>

        </div>
      </div>

      {/* Mobile Sticky CTA */}
      <div className="gm-mobile-cta">
        <div className="gm-mobile-cta-price">
          <span className="total-label">Total a pagar</span>
          <span className="total-value">{formatarMoeda(valorPrimeiraCobranca)}</span>
        </div>
        <button type="button" className={`gm-btn gm-btn-primary ${loading || checkoutUrl ? 'loading' : ''}`} disabled={!podePagar} onClick={iniciarPagamento}>
          {loading || checkoutUrl ? <SpinnerIcon /> : 'Pagar Agora'}
        </button>
      </div>

    </>
  );
}

function SummaryDetails({ planoSelecionado, valorMensalNormal, descontoPrimeiroMesAtivo, resumoPlano, cupomCodigo, setCupomCodigo, cupomAplicado, cupomErro, validarCupom, removerCupom, validandoCupom, showCoupon, setShowCoupon }) {
  return (
    <div className="gm-summary-card">
      <div className="gm-summary-plan-top">
        <div>
          <span className="gm-summary-kicker">Assinatura mensal</span>
          <strong className="gm-summary-plan-name">{planoSelecionado.nome}</strong>
        </div>
        <span className="gm-plan-badge">{planoSelecionado.destaque}</span>
      </div>

      <p className="gm-summary-description">{planoSelecionado.descricao}</p>

      <div className="gm-summary-price-block">
        <span className="gm-summary-price-label">
          {descontoPrimeiroMesAtivo ? 'Primeira cobrança' : 'Valor mensal'}
        </span>
        <div className="gm-summary-price">
          <strong>{formatarMoeda(resumoPlano.valorFinal)}</strong>
          <span>{descontoPrimeiroMesAtivo ? 'hoje' : '/mês'}</span>
        </div>

        {descontoPrimeiroMesAtivo && (
          <div className="gm-summary-discount">
            <span>Economia na primeira mensalidade</span>
            <strong>-{formatarMoeda(resumoPlano.descontoValor)}</strong>
          </div>
        )}
      </div>

      <div className="gm-summary-divider" />

      <div className="gm-summary-row">
        <span>Mensalidade normal</span>
        <strong>{formatarMoeda(valorMensalNormal)}</strong>
      </div>

      {descontoPrimeiroMesAtivo && (
        <div className="gm-summary-row gm-muted-row">
          <span>Próximas cobranças</span>
          <strong>{formatarMoeda(valorMensalNormal)}/mês</strong>
        </div>
      )}

      <div className="gm-summary-divider" />

      <div className="gm-summary-total">
        <span>Total hoje</span>
        <strong>{formatarMoeda(resumoPlano.valorFinal)}</strong>
      </div>

      <div className="gm-coupon-section">
        {!showCoupon && !cupomAplicado ? (
          <button type="button" className="gm-link-btn" onClick={() => setShowCoupon(true)}>
            <span>+</span> Adicionar cupom
          </button>
        ) : (
          <div className="gm-coupon-input-group">
            <input
              type="text"
              value={cupomCodigo}
              onChange={e => setCupomCodigo(e.target.value)}
              placeholder="Código promocional"
              className="gm-input gm-input-small"
              disabled={!!cupomAplicado || validandoCupom}
            />
            {cupomAplicado ? (
              <button type="button" className="gm-btn gm-btn-outline gm-btn-small" onClick={removerCupom}>Remover</button>
            ) : (
              <button type="button" className="gm-btn gm-btn-secondary gm-btn-small" onClick={validarCupom} disabled={validandoCupom || !cupomCodigo}>
                {validandoCupom ? <SpinnerIcon /> : 'Aplicar'}
              </button>
            )}
          </div>
        )}

        {cupomErro && <span className="gm-coupon-msg error fade-in">{cupomErro}</span>}
        {cupomAplicado && <span className="gm-coupon-msg success fade-in"><CheckIcon /> Cupom aplicado</span>}
      </div>

      <div className="gm-payment-trust">
        <div className="gm-payment-trust-icon"><LockIcon /></div>
        <div>
          <strong>Pagamento seguro</strong>
          <span>Você será direcionado ao Mercado Pago para concluir.</span>
        </div>
      </div>
    </div>
  );
}

/* === Minimalist Icons (Material Style) === */
function LockIcon({ color = "currentColor" }) { return <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: 16, height: 16}}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>; }
function CartIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: 18, height: 18}}><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>; }
function ChevronIcon({ isOpen }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: 16, height: 16, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none'}}><polyline points="6 9 12 15 18 9"/></svg>; }
function CheckIcon({ color = 'currentColor' }) { return <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{width: 14, height: 14}}><polyline points="20 6 9 17 4 12"/></svg>; }
function CheckCircleIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: 20, height: 20, flexShrink:0}}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>; }
function AlertIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: 20, height: 20, flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function InfoIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: 20, height: 20, flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>; }
function SpinnerIcon({ className }) { return <svg className={`gm-spinner-svg ${className || ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width: 18, height: 18}}><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>; }
function EyeIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: 18, height: 18}}><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>; }
function EyeOffIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: 18, height: 18}}><path d="M17.94 17.94A10.94 10.94 0 0112 19C5 19 1 12 1 12a20.3 20.3 0 015.06-5.94M9.9 4.24A10.6 10.6 0 0112 4c7 0 11 8 11 8a20.8 20.8 0 01-3.17 4.19M14.12 14.12A3 3 0 019.88 9.88M1 1l22 22"/></svg>; }

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  :root {
    --gm-bg: #ffffff;
    --gm-surface: #ffffff;
    --gm-soft: #f7f9f9;
    --gm-soft-2: #f1f4f3;

    --gm-line: #e8ecec;
    --gm-line-strong: #d8dfde;

    --gm-text: #172020;
    --gm-text-2: #374642;
    --gm-muted: #74807d;
    --gm-soft-text: #9aa3a1;

    --gm-accent: #36564c;
    --gm-accent-hover: #2e4a42;
    --gm-accent-soft: #eef4f2;

    --gm-blue: #4285f4;
    --gm-error: #d93025;
    --gm-error-soft: #fff6f5;
    --gm-success: #188038;
    --gm-success-soft: #f2f7f5;
    --gm-warning: #8a6410;
    --gm-warning-soft: #fffaf0;
    --gm-yellow: #fbbc04;

    --font-ui: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  html {
    min-height: 100%;
    background: #fff;
    scroll-behavior: smooth;
  }

  body {
    margin: 0;
    min-height: 100%;
    background: #fff;
    color: var(--gm-text);
    font-family: var(--font-ui);
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
  }

  a {
    color: var(--gm-accent);
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  button,
  input {
    font: inherit;
  }

  button {
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  button:focus-visible,
  input:focus-visible,
  a:focus-visible {
    outline: 2px solid rgba(54,86,76,.18);
    outline-offset: 2px;
  }

  /* =========================================================
     LOADER — FLAT
  ========================================================= */
  .gm-loader-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: grid;
    place-items: center;
    background: rgba(255,255,255,.98);
    opacity: 0;
    visibility: hidden;
    transition: opacity .18s ease, visibility .18s ease;
  }

  .gm-loader-overlay.active {
    opacity: 1;
    visibility: visible;
  }

  .gm-loader-content {
    min-width: 220px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  .gm-loader-text {
    margin: 0;
    color: var(--gm-muted);
    font-size: 10px;
    font-weight: 650;
  }

  .gm-spinner-large {
    width: 27px !important;
    height: 27px !important;
    color: var(--gm-accent);
  }

  .gm-spinner-svg {
    animation: gmSpin .8s linear infinite;
  }

  @keyframes gmSpin {
    to { transform: rotate(360deg); }
  }

  .fade-in {
    animation: gmFadeIn .16s ease both;
  }

  @keyframes gmFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* =========================================================
     HEADER — MESMA LINGUAGEM DO SISTEMA
  ========================================================= */
  .gm-header {
    position: sticky;
    top: 0;
    z-index: 100;
    height: 60px;
    display: flex;
    align-items: center;
    background: #fff;
    border-bottom: 1px solid var(--gm-line);
    box-shadow: none;
    backdrop-filter: none;
  }

  .gm-header-inner {
    width: min(1180px, calc(100% - 40px));
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  .gm-logo {
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--gm-text);
    text-decoration: none !important;
  }

  .gm-logo img {
    width: 27px;
    height: 27px;
    object-fit: contain;
  }

  .gm-brand-wordmark {
    font-size: 15px;
    line-height: 1;
    font-weight: 800;
    letter-spacing: -.035em;
    white-space: nowrap;
  }

  .gm-secure-badge {
    min-height: 30px;
    padding: 0 9px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--gm-line);
    border-radius: 6px;
    background: #fff;
    color: var(--gm-muted);
    font-size: 8.5px;
    font-weight: 650;
    white-space: nowrap;
    box-shadow: none;
  }

  .gm-secure-badge svg {
    width: 13px !important;
    height: 13px !important;
  }

  .gm-secure-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--gm-success);
    box-shadow: none;
  }

  /* =========================================================
     PAGE / COLUMNS
  ========================================================= */
  .gm-layout {
    width: min(1180px, calc(100% - 40px));
    margin: 0 auto;
    padding: 34px 0 58px;
  }

  .gm-columns {
    display: grid;
    grid-template-columns: minmax(0,1fr) 350px;
    gap: 48px;
    align-items: start;
  }

  .gm-main {
    min-width: 0;
    max-width: 720px;
  }

  .gm-title-area {
    margin-bottom: 25px;
    padding-bottom: 21px;
    border-bottom: 1px solid var(--gm-line);
  }

  .gm-eyebrow {
    margin-bottom: 7px;
    color: var(--gm-muted);
    font-size: 8px;
    line-height: 1;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .06em;
  }

  .gm-eyebrow-dot {
    display: none;
  }

  .gm-h1 {
    margin: 0 0 5px;
    max-width: 620px;
    color: var(--gm-text);
    font-size: 25px;
    line-height: 1.15;
    font-weight: 750;
    letter-spacing: -.035em;
  }

  .gm-p {
    margin: 0;
    max-width: 600px;
    color: var(--gm-muted);
    font-size: 10.5px;
    line-height: 1.6;
    font-weight: 500;
  }

  .gm-trust-row {
    margin-top: 13px;
    display: flex;
    flex-wrap: wrap;
    gap: 9px 16px;
  }

  .gm-trust-row span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: var(--gm-muted);
    font-size: 8px;
    font-weight: 600;
  }

  .gm-trust-row svg {
    width: 12px !important;
    height: 12px !important;
    color: var(--gm-success);
  }

  /* =========================================================
     ALERTS
  ========================================================= */
  .gm-alert {
    margin-bottom: 15px;
    padding: 10px 11px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    border: 1px solid;
    border-radius: 6px;
    font-size: 9px;
    line-height: 1.5;
    font-weight: 600;
    box-shadow: none;
  }

  .gm-alert svg {
    width: 14px !important;
    height: 14px !important;
  }

  .gm-alert p {
    color: inherit;
  }

  .gm-alert-error {
    color: #a93c34;
    background: var(--gm-error-soft);
    border-color: #ead8d5;
  }

  .gm-alert-success {
    color: #2d6a55;
    background: var(--gm-success-soft);
    border-color: #dce9e4;
  }

  .gm-alert-warning {
    color: var(--gm-warning);
    background: var(--gm-warning-soft);
    border-color: #eadfbf;
  }

  /* =========================================================
     FORM — ETAPAS COMO SEÇÕES, NÃO CARDS
  ========================================================= */
  .gm-form-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0;
    border-top: 1px solid var(--gm-line);
  }

  .gm-card {
    position: relative;
    overflow: visible;
    padding: 21px 0;
    background: transparent;
    border: 0;
    border-bottom: 1px solid var(--gm-line);
    border-radius: 0;
    box-shadow: none;
    transition: opacity .12s ease, background .12s ease;
  }

  .gm-card:hover:not(.gm-card-disabled) {
    border-color: var(--gm-line);
    box-shadow: none;
  }

  .gm-card-disabled {
    opacity: .43;
    pointer-events: none;
    background: transparent;
  }

  .gm-card-done {
    border-color: var(--gm-line);
    background: transparent;
  }

  .gm-card-done::before {
    display: none;
  }

  .gm-card-header {
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 9px;
  }

  .gm-step-circle {
    width: 23px;
    height: 23px;
    flex: 0 0 23px;
    display: grid;
    place-items: center;
    border: 1px solid var(--gm-line-strong);
    border-radius: 4px;
    background: #fff;
    color: var(--gm-muted);
    font-size: 8px;
    font-weight: 750;
  }

  .gm-step-circle.done {
    color: #fff;
    border-color: var(--gm-accent);
    background: var(--gm-accent);
  }

  .gm-step-circle svg {
    width: 11px !important;
    height: 11px !important;
  }

  .gm-h2 {
    margin: 0;
    color: var(--gm-text);
    font-size: 12px;
    line-height: 1.3;
    font-weight: 750;
    letter-spacing: -.015em;
  }

  .gm-card-body {
    min-width: 0;
  }

  .gm-grid-2 {
    display: grid;
    grid-template-columns: repeat(2, minmax(0,1fr));
    gap: 13px;
  }

  .gm-input-group {
    min-width: 0;
  }

  .gm-input-group label,
  .gm-otp-box label {
    display: block;
    margin-bottom: 5px;
    color: var(--gm-text-2);
    font-size: 8.5px;
    line-height: 1.35;
    font-weight: 650;
  }

  .gm-input-action-row {
    display: grid;
    grid-template-columns: minmax(0,1fr) auto;
    gap: 7px;
  }

  .gm-input {
    width: 100%;
    height: 39px;
    padding: 0 10px;
    border: 1px solid var(--gm-line-strong);
    border-radius: 6px;
    outline: 0;
    background: #fff;
    color: var(--gm-text);
    font-size: 10px;
    font-weight: 500;
    box-shadow: none;
    transition: border-color .12s ease, background .12s ease;
  }

  .gm-input::placeholder {
    color: #a5adab;
    font-weight: 400;
  }

  .gm-input:hover:not(:disabled) {
    border-color: #c9d0cf;
  }

  .gm-input:focus {
    border-color: var(--gm-accent);
    box-shadow: 0 0 0 2px rgba(54,86,76,.07);
  }

  .gm-input:disabled {
    color: var(--gm-soft-text);
    background: #f5f6f6;
    cursor: not-allowed;
  }

  .gm-input-small {
    height: 34px;
    font-size: 8.5px;
  }

  .gm-pwd-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  .gm-pwd-wrapper .gm-input {
    padding-right: 38px;
  }

  .gm-pwd-toggle {
    position: absolute;
    right: 3px;
    width: 32px;
    height: 32px;
    padding: 0;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: var(--gm-soft-text);
  }

  .gm-pwd-toggle:hover {
    color: var(--gm-text-2);
    background: var(--gm-soft);
  }

  .gm-pwd-toggle svg {
    width: 14px !important;
    height: 14px !important;
  }

  .gm-otp-box {
    margin-top: 10px;
    padding: 12px 0 0;
    border-top: 1px solid var(--gm-line);
    background: transparent;
    border-radius: 0;
  }

  .gm-otp-input {
    letter-spacing: .20em;
    font-weight: 700;
  }

  .gm-otp-spinner {
    position: absolute;
    right: 10px;
    top: 50%;
    width: 14px !important;
    height: 14px !important;
    color: var(--gm-accent);
    transform: translateY(-50%);
  }

  /* =========================================================
     CHECKBOXES / TERMOS
  ========================================================= */
  .gm-checkbox-row {
    margin: 0;
    padding: 9px 0;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    border-bottom: 1px solid var(--gm-line);
    cursor: pointer;
  }

  .gm-checkbox-row:last-of-type {
    border-bottom: 0;
  }

  .gm-checkbox-row input {
    width: 14px;
    height: 14px;
    margin: 1px 0 0;
    accent-color: var(--gm-accent);
    flex: 0 0 auto;
  }

  .gm-checkbox-row span {
    color: var(--gm-muted);
    font-size: 8.5px;
    line-height: 1.5;
    font-weight: 500;
  }

  .gm-checkbox-row strong {
    color: var(--gm-text-2);
    font-weight: 700;
  }

  .gm-checkbox-row a {
    color: var(--gm-accent);
    font-weight: 650;
  }

  /* =========================================================
     BUTTONS
  ========================================================= */
  .gm-btn {
    min-height: 36px;
    padding: 0 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: 1px solid var(--gm-line-strong);
    border-radius: 6px;
    white-space: nowrap;
    cursor: pointer;
    font-size: 9px;
    line-height: 1;
    font-weight: 700;
    box-shadow: none;
    transition: background .12s ease, color .12s ease, border-color .12s ease, opacity .12s ease;
  }

  .gm-btn:hover:not(:disabled) {
    transform: none;
    box-shadow: none;
  }

  .gm-btn:disabled {
    opacity: .42;
    cursor: not-allowed;
  }

  .gm-btn-primary {
    color: #fff;
    background: var(--gm-accent);
    border-color: var(--gm-accent);
  }

  .gm-btn-primary:hover:not(:disabled) {
    color: #fff;
    background: var(--gm-accent-hover);
    border-color: var(--gm-accent-hover);
  }

  .gm-btn-outline,
  .gm-btn-secondary {
    color: var(--gm-text-2);
    background: #fff;
    border-color: var(--gm-line-strong);
  }

  .gm-btn-outline:hover:not(:disabled),
  .gm-btn-secondary:hover:not(:disabled) {
    color: var(--gm-text);
    background: var(--gm-soft);
    border-color: #c8d0ce;
  }

  .gm-btn-small {
    min-height: 31px;
    padding: 0 9px;
    font-size: 8px;
  }

  .gm-btn-large {
    min-height: 41px;
    padding: 0 15px;
    font-size: 9.5px;
  }

  .gm-btn.loading {
    pointer-events: none;
  }

  .gm-desktop-action {
    margin-top: 17px;
    padding-top: 16px;
    border-top: 1px solid var(--gm-line);
  }

  .gm-desktop-action .gm-btn {
    width: 100%;
  }

  .gm-footer-note {
    margin: 7px 0 0;
    color: var(--gm-soft-text);
    text-align: center;
    font-size: 7.5px;
    line-height: 1.45;
  }

  .gm-footer-note strong {
    color: var(--gm-muted);
  }

  /* =========================================================
     SIDEBAR / RESUMO — SEM CARD
  ========================================================= */
  .gm-sidebar {
    min-width: 0;
  }

  .gm-sidebar-sticky {
    position: sticky;
    top: 84px;
    padding-left: 26px;
    border-left: 1px solid var(--gm-line);
  }

  .gm-sidebar-title {
    margin: 0 0 13px;
    color: var(--gm-text);
    font-size: 11px;
    line-height: 1.3;
    font-weight: 750;
  }

  .gm-plan-selector {
    margin-bottom: 18px;
  }

  .gm-section-label-row {
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .gm-label-small {
    color: var(--gm-text-2);
    font-size: 8px;
    line-height: 1.2;
    font-weight: 700;
  }

  .gm-section-hint {
    color: var(--gm-soft-text);
    font-size: 7px;
    font-weight: 500;
  }

  .gm-plan-grid {
    display: grid;
    gap: 0;
    border-top: 1px solid var(--gm-line);
    border-bottom: 1px solid var(--gm-line);
  }

  .gm-plan-chip {
    min-height: 52px;
    padding: 8px 0;
    display: grid;
    grid-template-columns: 18px minmax(0,1fr) auto;
    align-items: center;
    gap: 8px;
    border: 0;
    border-bottom: 1px solid var(--gm-line);
    border-radius: 0;
    background: transparent;
    color: var(--gm-text);
    text-align: left;
    box-shadow: none;
    transition: background .12s ease;
  }

  .gm-plan-chip:last-child {
    border-bottom: 0;
  }

  .gm-plan-chip:hover {
    background: var(--gm-soft);
  }

  .gm-plan-chip.active {
    background: transparent;
  }

  .gm-plan-chip-check {
    width: 16px;
    height: 16px;
    display: grid;
    place-items: center;
    border: 1px solid var(--gm-line-strong);
    border-radius: 50%;
    color: #fff;
    background: #fff;
  }

  .gm-plan-chip.active .gm-plan-chip-check {
    border-color: var(--gm-accent);
    background: var(--gm-accent);
  }

  .gm-plan-chip-check svg {
    width: 9px !important;
    height: 9px !important;
  }

  .gm-plan-chip-copy {
    min-width: 0;
  }

  .gm-plan-chip-copy strong,
  .gm-plan-chip-copy small {
    display: block;
  }

  .gm-plan-chip-copy strong {
    color: var(--gm-text);
    font-size: 8.5px;
    line-height: 1.3;
    font-weight: 700;
  }

  .gm-plan-chip-copy small {
    margin-top: 2px;
    color: var(--gm-muted);
    font-size: 7px;
    line-height: 1.3;
  }

  .gm-plan-chip-price {
    color: var(--gm-text-2);
    text-align: right;
    font-size: 8.5px;
    font-weight: 700;
    white-space: nowrap;
  }

  .gm-plan-chip-price small {
    margin-left: 2px;
    color: var(--gm-soft-text);
    font-size: 6.5px;
    font-weight: 500;
  }

  .gm-summary-card {
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  .gm-summary-plan-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .gm-summary-kicker {
    display: block;
    margin-bottom: 3px;
    color: var(--gm-muted);
    font-size: 7px;
    line-height: 1;
    font-weight: 650;
    text-transform: uppercase;
    letter-spacing: .05em;
  }

  .gm-summary-plan-name {
    display: block;
    color: var(--gm-text);
    font-size: 13px;
    line-height: 1.2;
    font-weight: 750;
  }

  .gm-plan-badge {
    padding: 3px 5px;
    border: 1px solid var(--gm-line-strong);
    border-radius: 4px;
    color: var(--gm-muted);
    background: transparent;
    font-size: 6.5px;
    line-height: 1;
    font-weight: 700;
    white-space: nowrap;
  }

  .gm-summary-description {
    margin: 7px 0 14px;
    color: var(--gm-muted);
    font-size: 8px;
    line-height: 1.55;
  }

  .gm-summary-price-block {
    padding: 14px 0;
    border-top: 1px solid var(--gm-line);
  }

  .gm-summary-price-label {
    display: block;
    margin-bottom: 5px;
    color: var(--gm-muted);
    font-size: 7px;
    font-weight: 650;
  }

  .gm-summary-price {
    display: flex;
    align-items: baseline;
    gap: 4px;
  }

  .gm-summary-price strong {
    color: var(--gm-text);
    font-size: 25px;
    line-height: 1;
    font-weight: 750;
    letter-spacing: -.035em;
  }

  .gm-summary-price span {
    color: var(--gm-muted);
    font-size: 7.5px;
    font-weight: 500;
  }

  .gm-summary-discount {
    margin-top: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    color: var(--gm-success);
    font-size: 7.5px;
  }

  .gm-summary-discount strong {
    font-weight: 700;
  }

  .gm-summary-divider {
    height: 1px;
    margin: 0;
    background: var(--gm-line);
  }

  .gm-summary-row,
  .gm-summary-total {
    min-height: 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border-bottom: 1px solid var(--gm-line);
  }

  .gm-summary-row span,
  .gm-summary-total span {
    color: var(--gm-muted);
    font-size: 7.5px;
  }

  .gm-summary-row strong {
    color: var(--gm-text-2);
    font-size: 8px;
    font-weight: 700;
  }

  .gm-muted-row {
    color: var(--gm-soft-text);
  }

  .gm-summary-total strong {
    color: var(--gm-text);
    font-size: 11px;
    font-weight: 750;
  }

  /* =========================================================
     CUPOM
  ========================================================= */
  .gm-coupon-section {
    padding: 13px 0;
    border-bottom: 1px solid var(--gm-line);
  }

  .gm-link-btn {
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--gm-accent);
    font-size: 8px;
    font-weight: 700;
  }

  .gm-link-btn:hover {
    text-decoration: underline;
  }

  .gm-coupon-input-group {
    display: grid;
    grid-template-columns: minmax(0,1fr) auto;
    gap: 6px;
  }

  .gm-coupon-msg {
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 7px;
    line-height: 1.4;
    font-weight: 650;
  }

  .gm-coupon-msg.error {
    color: var(--gm-error);
  }

  .gm-coupon-msg.success {
    color: var(--gm-success);
  }

  .gm-coupon-msg svg {
    width: 10px !important;
    height: 10px !important;
  }

  /* =========================================================
     PAYMENT TRUST
  ========================================================= */
  .gm-payment-trust {
    padding: 13px 0 0;
    display: grid;
    grid-template-columns: 24px 1fr;
    gap: 8px;
    align-items: start;
    background: transparent;
    border: 0;
    border-radius: 0;
  }

  .gm-payment-trust-icon {
    width: 24px;
    height: 24px;
    display: grid;
    place-items: center;
    color: var(--gm-accent);
    background: transparent;
  }

  .gm-payment-trust-icon svg {
    width: 13px !important;
    height: 13px !important;
  }

  .gm-payment-trust strong,
  .gm-payment-trust span {
    display: block;
  }

  .gm-payment-trust strong {
    margin-bottom: 2px;
    color: var(--gm-text-2);
    font-size: 8px;
    font-weight: 700;
  }

  .gm-payment-trust span {
    color: var(--gm-muted);
    font-size: 7px;
    line-height: 1.4;
  }

  /* =========================================================
     MOBILE SUMMARY
  ========================================================= */
  .gm-mobile-summary {
    display: none;
    margin-bottom: 18px;
    border-top: 1px solid var(--gm-line);
    border-bottom: 1px solid var(--gm-line);
    background: #fff;
  }

  .gm-mobile-summary-toggle {
    width: 100%;
    min-height: 46px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border: 0;
    background: transparent;
    color: var(--gm-text);
  }

  .toggle-left {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 7px;
    color: var(--gm-muted);
    font-size: 8px;
    font-weight: 650;
  }

  .toggle-left svg {
    width: 14px !important;
    height: 14px !important;
  }

  .toggle-right {
    color: var(--gm-text);
    font-size: 10px;
    font-weight: 750;
    white-space: nowrap;
  }

  .gm-summary-content {
    padding: 14px 0;
    border-top: 1px solid var(--gm-line);
    background: transparent;
  }

  .gm-summary-content .gm-plan-selector {
    margin: 0 0 16px;
  }

  /* =========================================================
     MOBILE CTA — SEM SOMBRA / SEM BLUR
  ========================================================= */
  .gm-mobile-cta {
    display: none;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 90;
    padding: 8px 12px calc(8px + env(safe-area-inset-bottom));
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: #fff;
    border-top: 1px solid var(--gm-line-strong);
    box-shadow: none;
    backdrop-filter: none;
  }

  .gm-mobile-cta-price {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .total-label {
    color: var(--gm-muted);
    font-size: 7px;
    font-weight: 650;
    text-transform: uppercase;
    letter-spacing: .05em;
  }

  .total-value {
    color: var(--gm-text);
    font-size: 13px;
    line-height: 1.1;
    font-weight: 750;
    letter-spacing: -.02em;
  }

  /* =========================================================
     RESPONSIVE
  ========================================================= */
  @media (max-width: 980px) {
    .gm-header-inner,
    .gm-layout {
      width: min(760px, calc(100% - 28px));
    }

    .gm-layout {
      padding: 28px 0 92px;
    }

    .gm-columns {
      display: block;
    }

    .gm-main {
      max-width: none;
    }

    .gm-sidebar {
      display: none;
    }

    .gm-desktop-action {
      display: none;
    }

    .gm-mobile-summary {
      display: block;
    }

    .gm-mobile-cta {
      display: flex;
    }

    .gm-mobile-cta .gm-btn {
      min-width: 132px;
      min-height: 36px;
    }
  }

  @media (max-width: 640px) {
    .gm-header {
      height: 56px;
    }

    .gm-header-inner,
    .gm-layout {
      width: calc(100% - 24px);
    }

    .gm-logo img {
      width: 25px;
      height: 25px;
    }

    .gm-brand-wordmark {
      font-size: 14px;
    }

    .gm-secure-badge {
      min-height: 28px;
      padding: 0 7px;
      font-size: 0;
    }

    .gm-secure-badge svg {
      width: 13px !important;
      height: 13px !important;
    }

    .gm-layout {
      padding-top: 20px;
      padding-bottom: 88px;
    }

    .gm-title-area {
      margin-bottom: 18px;
      padding-bottom: 17px;
    }

    .gm-eyebrow {
      margin-bottom: 6px;
      font-size: 7.5px;
    }

    .gm-h1 {
      font-size: 22px;
      line-height: 1.12;
    }

    .gm-p {
      font-size: 9.5px;
    }

    .gm-trust-row {
      margin-top: 11px;
      gap: 7px 12px;
    }

    .gm-trust-row span {
      font-size: 7.5px;
    }

    .gm-card {
      padding: 18px 0;
    }

    .gm-card-header {
      margin-bottom: 14px;
    }

    .gm-h2 {
      font-size: 11px;
    }

    .gm-grid-2 {
      grid-template-columns: 1fr;
      gap: 12px;
    }

    .gm-input-action-row {
      grid-template-columns: 1fr;
      gap: 6px;
    }

    .gm-input-action-row .gm-btn {
      width: 100%;
    }

    .gm-input {
      height: 39px;
      font-size: 9.5px;
    }

    .gm-checkbox-row {
      padding: 8px 0;
    }

    .gm-checkbox-row span {
      font-size: 8px;
    }

    .gm-alert {
      padding: 9px 10px;
      font-size: 8.5px;
    }

    .gm-mobile-cta .gm-btn {
      min-width: 116px;
      padding: 0 10px;
      font-size: 8.5px;
    }

    .total-value {
      font-size: 12px;
    }

    .gm-plan-chip {
      min-height: 50px;
    }
  }

  @media (max-width: 390px) {
    .gm-header-inner,
    .gm-layout {
      width: calc(100% - 20px);
    }

    .gm-brand-wordmark {
      font-size: 13.5px;
    }

    .gm-h1 {
      font-size: 20px;
    }

    .gm-plan-chip {
      grid-template-columns: 18px minmax(0,1fr);
    }

    .gm-plan-chip-price {
      grid-column: 2;
      justify-self: start;
      margin-top: -1px;
      text-align: left;
    }

    .gm-mobile-cta .gm-btn {
      min-width: 108px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      scroll-behavior: auto !important;
      animation-duration: .01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: .01ms !important;
    }
  }
`;
